import type { ReactNode } from "react";
import { catcat as cc } from "cerulean";
import { useLocalization } from "@fluent/react";
import {
  Table,
  useBreakpoints,
  TruncatedAccount,
  Row,
} from "@narmi/design_system";
import { FREQUENCIES } from "byzantine";
import {
  DisplayStatus,
  showAmountStrikeThrough,
  getOriginalPaymentDirection,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import Filters from "byzantine/src/filters";
import styles from "../PaymentActivity.module.scss";
import StatusTag from "./StatusTag";

// lowercase, in-sentence frequency fragments (e.g. "Send monthly to John").
const FREQUENCY_DESCRIPTORS: Record<string, string> = {
  [FREQUENCIES.ONCE]: "frequency-descriptor-once",
  [FREQUENCIES.DAILY]: "frequency-descriptor-daily",
  [FREQUENCIES.WEEKLY]: "frequency-descriptor-weekly",
  [FREQUENCIES.EVERY_TWO_WEEKS]: "frequency-descriptor-every-two-weeks",
  [FREQUENCIES.FIRST_AND_FIFTEENTH]: "frequency-descriptor-first-and-fifteenth",
  [FREQUENCIES.MONTHLY]: "frequency-descriptor-monthly",
  [FREQUENCIES.YEARLY]: "frequency-descriptor-yearly",
};

export const PaymentActivityRowDescription = ({
  payment,
}: {
  payment: API.FormattedPaymentActivity;
}) => {
  const { l10n } = useLocalization();
  const { m, l } = useBreakpoints();
  const {
    displayStatus,
    recipient,
    scheduledDate,
    frequency,
    parent,
    isInstantPayment,
    isSameDayPayment,
    isCollectPayment,
    isInternationalWirePayment,
    isFedWirePayment,
    isReversal,
    reversal,
    destinationDetails,
  } = payment;
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;

  const getDirection = () => {
    if (isCollectPayment) return l10n.getString("collect-label");
    if (isInternationalWirePayment)
      return l10n.getString("international-wire-label");
    if (isFedWirePayment) return l10n.getString("fedwire-label");
    return l10n.getString("send-label");
  };

  const getConnectorWord = () =>
    (isCollectPayment
      ? l10n.getString("label-from")
      : l10n.getString("label-to")
    ).toLowerCase();

  const getDate = () => {
    if (isPendingApproval && !scheduledDate) {
      return l10n.getString("label-processes-approval");
    }
    if (scheduledDate) {
      return Filters.longMonthDayYear(scheduledDate);
    }
    return "";
  };

  const getName = () => recipient?.name ?? "";

  // "Reverse collect from John" / "Reverse send to John"
  if (isReversal) {
    const recipientName =
      destinationDetails?.beneficiary_name || recipient?.name || "";
    return (
      <div className={cc(styles.ellipsisWrapper, { "margin--left--l": m })}>
        <span>
          {l10n.getString("approval-drawer-reversal-title", {
            transactionType:
              getOriginalPaymentDirection(payment) === "collect"
                ? "debit"
                : "credit",
            recipientName,
          })}
        </span>
        <div className="fontSize--s fontColor--secondary">{getDate()}</div>
      </div>
    );
  }

  const getDescription = () => {
    if (!(m || l)) {
      // "to John" or "from John"
      return `${getConnectorWord()} ${getName()}`;
    }

    // "monthly to John" / "on the 1st and 15th to John"
    if (!parent && frequency) {
      const descriptorId = FREQUENCY_DESCRIPTORS[frequency];
      // "on the" for 1st and 15th is part of the message, not concatenated here
      const descriptor = descriptorId
        ? l10n.getString(descriptorId)
        : frequency.toLowerCase();

      return `${descriptor} ${getConnectorWord()} ${getName()}`;
    }

    // "instantly to John" or "instantly from John"
    if (isInstantPayment) {
      return `${l10n.getString(
        "instantly-label"
      )} ${getConnectorWord()} ${getName()}`;
    }

    // "same-day from John" or "same-day to John"
    if (isSameDayPayment) {
      return `${l10n.getString(
        "same-day-label"
      )} ${getConnectorWord()} ${getName()}`;
    }

    // "to John"  or "from John"
    return `${getConnectorWord()} ${getName()}`;
  };

  return (
    <div className={cc(styles.ellipsisWrapper, { "margin--left--l": m })}>
      <span>{getDirection()} </span>
      <span>{getDescription()}</span>
      {!isReversal && reversal ? (
        <span className="margin--left--xs narmi-icon-rotate-ccw fontColor--secondary" />
      ) : null}
      <div className="fontSize--s fontColor--secondary">{getDate()}</div>
    </div>
  );
};

export type PaymentActivityRowProps = {
  payment: API.FormattedPaymentActivity;
  onRowClick: () => void;
  /** Optional leading cell (e.g. a selection checkbox). When omitted, no leading cell is rendered. */
  selectionCell?: ReactNode;
  /** Optional trailing cell (e.g. a kebab menu). When omitted, no trailing cell is rendered. */
  actionCell?: ReactNode;
};

const PaymentActivityRow = ({
  payment,
  onRowClick,
  selectionCell,
  actionCell,
}: PaymentActivityRowProps) => {
  const { m, l } = useBreakpoints();
  const {
    uuid,
    displayStatus,
    institutionAccount,
    displayAmountDollars,
    isAmountEstimated,
  } = payment;
  const showStrikeThrough = showAmountStrikeThrough(displayStatus);

  return (
    <Table.Row key={`table-row__${uuid}`} onRowClick={onRowClick}>
      {selectionCell}
      <Table.Cell>
        <Row>
          {!(m || l) ? (
            <Row.Item shrink>
              <StatusTag status={displayStatus} isBadge />{" "}
            </Row.Item>
          ) : null}
          <Row.Item>
            <PaymentActivityRowDescription payment={payment} />
          </Row.Item>
        </Row>
      </Table.Cell>
      <Table.Cell>
        <StatusTag status={displayStatus} isBadge={false} />
      </Table.Cell>
      <Table.Cell>
        <TruncatedAccount
          name={institutionAccount?.name ?? ""}
          lastFour={institutionAccount?.getMaskedNumber() ?? ""}
        />
      </Table.Cell>
      <Table.Cell textAlign={"right" as any}>
        <div className={styles.amountContent}>
          <span className={cc({ [styles.strikeThrough]: showStrikeThrough })}>
            {displayAmountDollars}
          </span>
          {isAmountEstimated ? (
            <span className="fontSize--s fontColor--secondary">Est. USD</span>
          ) : null}
        </div>
      </Table.Cell>
      {actionCell}
    </Table.Row>
  );
};

export default PaymentActivityRow;
